import { AclModel } from '../models/acl';
import { Injectable, inject } from '@angular/core';
import { ConfigData } from '../interfaces/config-data';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { BehaviorSubject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AuthenticationService } from '../auth/authentication.service';
import { TokenStorage } from '../auth/token-storage.service';
import { isNullOrUndefined } from '../../content/pages/components/general/event/shared/validator/undefined-or-null';

@Injectable({
  providedIn: 'root',
})
export class AclService implements ConfigData {
  private roleService = inject(NgxRolesService);
  private permService = inject(NgxPermissionsService);
  private authService = inject(AuthenticationService);
  private tokenStorage = inject(TokenStorage);

  public aclModel: AclModel;
  public onAclUpdated$: BehaviorSubject<AclModel>;

  initialize() {
    // set initial permission model
    this.aclModel = new AclModel();
    this.onAclUpdated$ = new BehaviorSubject(this.aclModel);

    // --- INVOICE REDIRECT: Clear stale localStorage early ---
    // If the current URL has a returnUrl pointing to a billing invoice page,
    // wipe auth-related localStorage entries BEFORE getUserRoles() reads them.
    // This is critical because ngxPermissionsGuard on /auth checks roles from
    // localStorage — if stale ADMIN/OWNER roles are present, the guard silently
    // redirects the user to the dashboard instead of letting the login page load.
    const hrefLower = location.href.toLowerCase();
    if (
      hrefLower.includes('returnurl') && (
        hrefLower.includes('ticketingfeeinvoice') ||
        hrefLower.includes('hostinginvoice') ||
        hrefLower.includes('hostingfeeinvoice')
      )
    ) {
      this.tokenStorage.clearInvoiceRedirectLocalStorage();
    }
    // ---------------------------------------------------------

    this.authService.getUserRoles().subscribe(roles => {
      if (
        location.href.toLowerCase().includes('userid') &&
        location.href.toLowerCase().includes('appid')
      ) {
        localStorage.clear();
      } else {
        this.setCurrrentUserRoles(roles);
      }
      //var pathname = this.tokenStorage.getPathName();
      var sslDomain = sessionStorage.getItem('sslDomain');
      if (isNullOrUndefined(sslDomain)) {
        if (
          location.pathname != '/auth' &&
          location.pathname != '/404' &&
          location.pathname != '/403' &&
          !location.pathname.includes('reset')
        )
          this.tokenStorage.setPathName(location.pathname + location.search);
        else localStorage.removeItem('pathName');
      }
    });

    // subscribe to credential changed, eg. after login response
    this.authService.onCredentialUpdated$
      .pipe(mergeMap(() => this.authService.getUserRoles()))
      .subscribe(roles => this.setCurrrentUserRoles(roles));

    // subscribe to acl data observable
    this.onAclUpdated$.subscribe(acl => {
      const permissions = Object.keys(acl.permissions).map(key => {
        return acl.permissions[key];
      });
      // load default permission list
      this.permService.loadPermissions(
        permissions,
        (permissionName, permissionStore) => !!permissionStore[permissionName]
      );

      // merge current user roles
      const roles = Object.assign({}, this.aclModel.currentUserRoles, {
        // default user role is GUEST
        GUEST: () => {
          // return this.authService.isAuthorized().toPromise();
        },
      });
      // add to role service
      this.roleService.addRoles(roles);
    });
  }

  /**
   * Set AclModel and fire off event that all subscribers will listen to
   * @param aclModel
   */
  setModel(aclModel: AclModel): any {
    aclModel = Object.assign({}, this.aclModel, aclModel);
    this.onAclUpdated$.next(aclModel);
  }

  setCurrrentUserRoles(roles: any): any {
    // update roles if the credential data has roles
    if (roles != null) {
      this.aclModel.currentUserRoles = {};
      roles.forEach(role => {
        this.aclModel.currentUserRoles[role] = this.aclModel.permissions[role];
      });
      // set updated acl model back to service
      this.setModel(this.aclModel);
    }
  }
}
